import { ref, shallowRef, reactive, computed, watch, type Ref } from 'vue';
import type { GridSortEntry, ItpDataGridColumn, SortDirection } from './types';
import { columnKey, queryValueReader } from './gridCell';
import { aggregateRows, isSubtotalColumn } from './gridAggregates';

export type ItpDataGridItems = readonly any[] | ItpDataGridFetcher;

export type ItpDataGridFetcher = () => any[] | Promise<any[]>;

export function isFetcher(
  items: ItpDataGridItems | undefined,
): items is ItpDataGridFetcher {
  return typeof items === 'function';
}

export function isConnectionError(error: unknown): boolean {
  const failed = error as { request?: unknown; response?: unknown } | null;
  if (failed?.response) {
    return false;
  }
  return Boolean(failed?.request) || globalThis.navigator?.onLine === false;
}

// ── Data source: array or fetcher + loading state ──
export function useGridData(
  items: Ref<ItpDataGridItems | undefined>,
  controlledLoading: Ref<boolean>,
  onLoadStart?: () => (() => void) | undefined,
) {
  const fetchedRows = shallowRef<readonly any[]>([]);
  const fetching = ref(false);
  const error = ref<unknown>(null);
  const stale = ref(false);

  const ownsData = computed(() => isFetcher(items.value));

  const rows = computed<readonly any[]>(() =>
    ownsData.value
      ? fetchedRows.value
      : ((items.value as readonly any[] | undefined) ?? []),
  );

  let latestRequest = 0;

  async function load(): Promise<void> {
    const fetch = items.value;
    if (!isFetcher(fetch)) {
      return;
    }
    const request = ++latestRequest;
    fetching.value = true;
    error.value = null;
    const finishLoad = onLoadStart?.();
    try {
      const result = (await fetch()) ?? [];
      if (request !== latestRequest) {
        return;
      }
      fetchedRows.value = result;
      stale.value = false;
    } catch (err) {
      if (request !== latestRequest) {
        return;
      }
      error.value = err;
      stale.value = fetchedRows.value.length > 0;
      console.error('[ItpDataGrid] failed to load items', err);
    } finally {
      if (request === latestRequest) {
        fetching.value = false;
      }
      finishLoad?.();
    }
  }

  watch(
    items,
    (value) => {
      if (isFetcher(value)) {
        void load();
      }
    },
    { immediate: true },
  );

  const loading = computed(() => fetching.value || controlledLoading.value);

  return { rows, loading, load, ownsData, error, stale };
}

export interface HeaderSortState {
  direction: SortDirection | null;
  priority: number | null;
}

function isSortableScalar(value: any): boolean {
  return value == null || typeof value !== 'object' || value instanceof Date;
}

// ── Sort: stackable 3-state cycle + locale-aware comparator ──
export function useGridSort(
  columns: Ref<ItpDataGridColumn[]>,
  locale?: Ref<string>,
  formatterText?: FilterTextLookup,
) {
  const sortEntries = ref<GridSortEntry[]>([]);

  function columnByKey(key: string): ItpDataGridColumn | undefined {
    return columns.value.find((column) => columnKey(column) === key);
  }

  function isSortable(field: string): boolean {
    return columnByKey(field)?.sortable !== false;
  }

  function cycleSort(field: string): void {
    if (!isSortable(field)) {
      return;
    }
    const existing = sortEntries.value.find((entry) => entry.field === field);
    if (!existing) {
      sortEntries.value = [...sortEntries.value, { field, direction: 'asc' }];
    } else if (existing.direction === 'asc') {
      sortEntries.value = sortEntries.value.map((entry) =>
        entry.field === field ? { field, direction: 'desc' } : entry,
      );
    } else {
      sortEntries.value = sortEntries.value.filter(
        (entry) => entry.field !== field,
      );
    }
  }

  function clearSort(): void {
    sortEntries.value = [];
  }

  function headerSortState(field: string): HeaderSortState {
    const index = sortEntries.value.findIndex((entry) => entry.field === field);
    if (index === -1) {
      return { direction: null, priority: null };
    }
    return {
      direction: sortEntries.value[index].direction,
      priority: sortEntries.value.length > 1 ? index + 1 : null,
    };
  }

  const comparator = computed<((a: any, b: any) => number) | null>(() => {
    const entries = sortEntries.value;
    if (!entries.length) {
      return null;
    }
    const collator = collatorFor(locale?.value);

    const specs = entries.map((entry) => {
      const column = columnByKey(entry.field);
      const read = queryValueReader(column, column?.field ?? entry.field);

      const displayed = new WeakMap<object, any>();
      const readSortable = (row: any): any => {
        const raw = read(row);
        if (isSortableScalar(raw)) {
          return raw;
        }
        if (!column || !formatterText || row == null) {
          return Array.isArray(raw) ? raw.join(', ') : raw;
        }
        if (displayed.has(row)) {
          return displayed.get(row);
        }
        const text =
          formatterText(row, column) ??
          (Array.isArray(raw) ? raw.join(', ') : raw);
        displayed.set(row, text);
        return text;
      };

      return { read: readSortable, direction: entry.direction };
    });
    return (rowA: any, rowB: any): number => {
      for (const spec of specs) {
        const result = compareValues(
          spec.read(rowA),
          spec.read(rowB),
          collator,
        );
        if (result !== 0) {
          return spec.direction === 'asc' ? result : -result;
        }
      }
      return 0;
    };
  });

  return {
    sortEntries,
    cycleSort,
    clearSort,
    headerSortState,
    isSortable,
    comparator,
  };
}

const COLLATOR_OPTIONS: Intl.CollatorOptions = {
  numeric: true,
  sensitivity: 'base',
};

const collators = new Map<string | undefined, Intl.Collator>();

export function collatorFor(locale: string | undefined): Intl.Collator {
  let collator = collators.get(locale);
  if (!collator) {
    collator = new Intl.Collator(locale, COLLATOR_OPTIONS);
    collators.set(locale, collator);
  }
  return collator;
}

const systemCollator = collatorFor(undefined);

export function compareValues(
  a: any,
  b: any,
  collator: Intl.Collator = systemCollator,
): number {
  if (a == null && b == null) {
    return 0;
  }
  if (a == null) {
    return -1;
  }
  if (b == null) {
    return 1;
  }

  const left = a instanceof Date ? a.getTime() : a;
  const right = b instanceof Date ? b.getTime() : b;

  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }

  return collator.compare(String(left), String(right));
}

export type FilterTextLookup = (
  row: any,
  column: ItpDataGridColumn,
) => string | undefined;

// ── Filter: per-column, formatter-aware text matching ──
export function useGridFilter(
  columns: Ref<ItpDataGridColumn[]>,
  cellText?: FilterTextLookup,
) {
  const filters = reactive<Record<string, string>>({});

  function columnByKey(key: string): ItpDataGridColumn | undefined {
    return columns.value.find((column) => columnKey(column) === key);
  }

  function isFilterable(field: string): boolean {
    return columnByKey(field)?.filterable !== false;
  }

  function setFilter(field: string, value: string): void {
    const trimmed = (value ?? '').trim();
    if (trimmed === '') {
      delete filters[field];
    } else {
      filters[field] = trimmed;
    }
  }

  function clearFilters(): void {
    for (const key of Object.keys(filters)) {
      delete filters[key];
    }
  }

  const hasActiveFilters = computed(() => Object.keys(filters).length > 0);

  const predicate = computed<((row: any) => boolean) | null>(() => {
    const specs = Object.entries(filters)
      .filter(([, value]) => value.trim() !== '')
      .map(([key, value]) => {
        const column = columnByKey(key);
        const read = queryValueReader(column, column?.field ?? key);
        const needle = searchable(value);
        const rendered = new WeakMap<object, string>();

        function displayed(row: any): string | undefined {
          if (!cellText || !column || row == null || typeof row !== 'object') {
            return undefined;
          }
          let text = rendered.get(row);
          if (text === undefined) {
            const shown = cellText(row, column);
            if (shown === undefined) {
              return undefined;
            }
            text = searchable(shown);
            rendered.set(row, text);
          }
          return text;
        }

        const rawIsSearchable =
          column?.formatter == null ||
          column.type === 'number' ||
          column.type === 'date';

        function matches(row: any): boolean {
          const text = displayed(row);
          if (text === undefined) {
            return cellMatches(read(row), needle);
          }
          return (
            text.includes(needle) ||
            (rawIsSearchable && cellMatches(read(row), needle))
          );
        }

        return { matches };
      });

    if (!specs.length) {
      return null;
    }

    return (row: any): boolean => specs.every((spec) => spec.matches(row));
  });

  return {
    filters,
    setFilter,
    clearFilters,
    isFilterable,
    hasActiveFilters,
    predicate,
  };
}

function searchable(text: string): string {
  return text.toLowerCase().replace(/\s+/g, ' ');
}

function cellMatches(cellValue: any, needle: string): boolean {
  if (cellValue == null) {
    return false;
  }
  const text = Array.isArray(cellValue)
    ? cellValue.join(', ')
    : String(cellValue);
  return searchable(text).includes(needle);
}

// ── Selection & subtotals ──
export function useGridSelection(
  rows: Ref<readonly any[]>,
  rowKey: Ref<string>,
  columns: Ref<ItpDataGridColumn[]>,
) {
  const selectedKeys = reactive(new Set<unknown>());

  let warnedAboutMissingKey = false;

  function keyOf(row: any): unknown {
    const key = row?.[rowKey.value];
    if (key == null && row != null && !warnedAboutMissingKey) {
      warnedAboutMissingKey = true;
      console.warn(
        `[ItpDataGrid] rows have no "${rowKey.value}" field, so selection cannot ` +
          'identify them and will do nothing. Set `rowKey` to a unique field.',
      );
    }
    return key;
  }

  function isSelected(row: any): boolean {
    return row != null && selectedKeys.has(keyOf(row));
  }

  function toggle(row: any): void {
    const key = keyOf(row);
    if (key == null) {
      return;
    }
    if (selectedKeys.has(key)) {
      selectedKeys.delete(key);
    } else {
      selectedKeys.add(key);
    }
  }

  function selectAll(): void {
    for (const row of rows.value) {
      const key = keyOf(row);
      if (key != null) {
        selectedKeys.add(key);
      }
    }
  }

  function clear(): void {
    selectedKeys.clear();
  }

  function toggleAll(): void {
    if (allSelected.value) {
      clear();
    } else {
      selectAll();
    }
  }

  const selectedCount = computed(() => selectedKeys.size);
  const fromSelection = computed(() => selectedKeys.size > 0);

  const selectedIds = computed<unknown[]>(() => [...selectedKeys]);

  const selectedItems = computed<any[]>(() =>
    selectedKeys.size === 0
      ? []
      : rows.value.filter((row) => selectedKeys.has(keyOf(row))),
  );

  const allSelected = computed(() => {
    const list = rows.value;
    return list.length > 0 && list.every((row) => selectedKeys.has(keyOf(row)));
  });

  const someSelected = computed(
    () => selectedKeys.size > 0 && !allSelected.value,
  );

  const totalsRows = computed<readonly any[]>(() =>
    selectedKeys.size === 0 ? rows.value : selectedItems.value,
  );

  const subtotalColumns = computed(() =>
    columns.value.filter(isSubtotalColumn),
  );

  const columnTotals = computed<Record<string, number>>(() =>
    aggregateRows(totalsRows.value, subtotalColumns.value),
  );

  return {
    selectedKeys,
    isSelected,
    toggle,
    selectAll,
    clear,
    toggleAll,
    selectedCount,
    selectedIds,
    selectedItems,
    fromSelection,
    allSelected,
    someSelected,
    subtotalColumns,
    columnTotals,
  };
}
